import type { PropsWithChildren } from "react";

import { Tooltip } from "@stacklet/ui";

import { useIsTruncated } from "app/hooks";

import { Link } from "./Link";

interface Props extends PropsWithChildren {
  to: string;
  description?: string;
  truncate?: boolean;
  /**
   * Only show the tooltip while the link text is actually clipped. Use when
   * the tooltip merely repeats the visible text, so it stays out of the way
   * once the full text fits.
   */
  tooltipOnlyWhenTruncated?: boolean;
}

export function TooltipLink({
  to,
  children,
  description,
  truncate = false,
  tooltipOnlyWhenTruncated = false,
}: Props) {
  const [textRef, isTruncated] = useIsTruncated<HTMLSpanElement>();

  if (truncate) {
    return (
      // Outer span establishes the containing block so the flex parent
      // (e.g. a DataGrid cell) clips the link before its own overflow fires.
      <span className="block w-full min-w-0 overflow-hidden">
        <Link className="block w-full min-w-0" to={to} truncate>
          {description ? (
            <Tooltip
              className="break-all"
              isDisabled={tooltipOnlyWhenTruncated && !isTruncated}
              title={description}
              asChild
            >
              {/* The tooltip trigger is width-constrained, so the ellipsis
                  must render here rather than on the <a> — the trigger is an
                  atomic inline box the <a>'s text-overflow cannot reach into.
                  This also keeps the tooltip anchored to the visible text. */}
              <span
                aria-label={description}
                className="block max-w-full truncate"
                data-testid={`tooltip-${to}`}
                ref={textRef}
              >
                {children}
              </span>
            </Tooltip>
          ) : (
            <span className="inline align-baseline">{children}</span>
          )}
        </Link>
      </span>
    );
  }

  if (description) {
    return (
      <Link to={to}>
        <Tooltip className="break-all" title={description} asChild>
          <span
            aria-label={description}
            className="inline align-baseline"
            data-testid={`tooltip-${to}`}
          >
            {children}
          </span>
        </Tooltip>
      </Link>
    );
  }

  return (
    <Link to={to}>
      <span className="inline align-baseline">{children}</span>
    </Link>
  );
}
